import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Zap, Plus, Trash2, Save, Loader2, BookOpen, ChevronDown, Copy, Check, Bot } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Client } from '@/types/client';
import { apiService } from '@/lib/api';

interface WebhookBoletoConfig {
  id?: number;
  client_id: number;
  is_enabled: boolean;
  trigger_words: string[];
  created_at?: string;
  updated_at?: string;
}

interface WebhooksTabProps {
  client: Client;
}

const WEBHOOK_URL =
  (import.meta.env.VITE_WEBHOOK_BOLETO_URL as string) || 'https://webhook.atomos.tech/webhooks/atomos';

const WebhooksTab: React.FC<WebhooksTabProps> = ({ client }) => {
  const { toast } = useToast();
  const [config, setConfig] = useState<WebhookBoletoConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [triggerWords, setTriggerWords] = useState<string[]>([]);
  const [newWord, setNewWord] = useState('');
  const [urlCopied, setUrlCopied] = useState(false);
  const [guideOpen, setGuideOpen] = useState(true);
  const [chatbotId, setChatbotId] = useState<string | null>(null);
  const [chatbotName, setChatbotName] = useState<string | null>(null);
  const [chatbots, setChatbots] = useState<Array<{ id: string; name: string }>>([]);
  const [chatbotsLoading, setChatbotsLoading] = useState(false);
  const [chatbotsError, setChatbotsError] = useState<string | null>(null);
  const [savingChatbot, setSavingChatbot] = useState(false);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(WEBHOOK_URL);
      setUrlCopied(true);
      toast({ title: 'Copiado!', description: 'URL copiada para a área de transferência.' });
      setTimeout(() => setUrlCopied(false), 2000);
    } catch {
      toast({ title: 'Erro', description: 'Não foi possível copiar a URL.', variant: 'destructive' });
    }
  };

  const loadConfig = async () => {
    if (!client?.id) return;
    try {
      setLoading(true);
      const res = await apiService.get(`/webhook-boleto-config/${client.id}`) as { success: boolean; data: WebhookBoletoConfig };
      const data = res.data;
      if (data) {
        setConfig(data);
        setIsEnabled(data.is_enabled ?? false);
        setTriggerWords(Array.isArray(data.trigger_words) ? [...data.trigger_words] : []);
      } else {
        setIsEnabled(false);
        setTriggerWords([]);
      }
    } catch (e: any) {
      console.error('[WebhooksTab] loadConfig:', e);
      setIsEnabled(false);
      setTriggerWords([]);
      toast({ title: 'Erro', description: 'Não foi possível carregar a configuração.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, [client?.id]);

  useEffect(() => {
    if (!client?.id) return;
    let aborted = false;
    const loadChatbots = async () => {
      setChatbotsLoading(true);
      setChatbotsError(null);
      try {
        const cfgResp = await apiService.get<{ success: boolean; config: { chatbot_id?: string | null; chatbot_name?: string | null } | null }>(
          `/atomos-chat/client/${client.id}/config`
        );
        if (!aborted && cfgResp?.config) {
          setChatbotId(cfgResp.config.chatbot_id ?? null);
          setChatbotName(cfgResp.config.chatbot_name ?? null);
        }
        const listResp = await apiService.get<{ success: boolean; chatbots: Array<{ id: string; name: string }> }>(
          `/atomos-chat/client/${client.id}/chatbots`
        );
        if (!aborted) setChatbots(listResp.chatbots || []);
      } catch (err: any) {
        if (!aborted) {
          const status = err?.status ?? err?.response?.status;
          if (status === 409) {
            setChatbotsError('Configure a conexão AtomosChat antes de escolher um chatbot.');
          } else {
            setChatbotsError('Não foi possível carregar os chatbots.');
          }
        }
      } finally {
        if (!aborted) setChatbotsLoading(false);
      }
    };
    loadChatbots();
    return () => {
      aborted = true;
    };
  }, [client?.id]);

  const handleSaveChatbot = async () => {
    if (!client?.id) return;
    setSavingChatbot(true);
    try {
      const selected = chatbots.find((b) => b.id === chatbotId);
      await apiService.put(`/atomos-chat/client/${client.id}/config`, {
        account_name: client.trade_name || client.legal_name || 'default',
        chatbot_id: chatbotId || null,
        chatbot_name: selected?.name || null,
      });
      setChatbotName(selected?.name || null);
      toast({
        title: 'Chatbot salvo',
        description: selected?.name ? `Chatbot "${selected.name}" será usado nos envios.` : 'Nenhum chatbot selecionado (comportamento padrão).',
      });
    } catch (err: any) {
      toast({
        title: 'Erro ao salvar chatbot',
        description: err?.message || 'Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setSavingChatbot(false);
    }
  };

  const handleSave = async () => {
    if (!client?.id) return;
    try {
      setSaving(true);
      await apiService.put(`/webhook-boleto-config/${client.id}`, {
        is_enabled: isEnabled,
        trigger_words: triggerWords,
      });
      await loadConfig();
      toast({ title: 'Sucesso', description: 'Configuração de webhook salva com sucesso.' });
    } catch (e: any) {
      console.error('[WebhooksTab] handleSave:', e);
      toast({ title: 'Erro', description: e?.message ?? 'Erro ao salvar configuração.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const addWord = () => {
    const trimmed = newWord.trim();
    if (!trimmed) return;
    if (triggerWords.includes(trimmed)) {
      toast({ title: 'Aviso', description: 'Palavra já adicionada.', variant: 'destructive' });
      return;
    }
    setTriggerWords((prev) => [...prev, trimmed]);
    setNewWord('');
  };

  const removeWord = (index: number) => {
    setTriggerWords((prev) => prev.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Guia passo a passo */}
      <Collapsible open={guideOpen} onOpenChange={setGuideOpen}>
        <Card className="border-2 border-blue-200 bg-blue-50/50">
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-blue-50/80 transition-colors rounded-t-lg">
              <CardTitle className="flex items-center justify-between text-blue-800">
                <span className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  Como ativar esta integração
                </span>
                <ChevronDown className={`w-5 h-5 transition-transform ${guideOpen ? 'rotate-180' : ''}`} />
              </CardTitle>
              <CardDescription className="text-blue-700">
                Siga o passo a passo para configurar o webhook na AtomosChat e ativar a automação de envio de boletos.
              </CardDescription>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-5 pt-0">
              <ol className="list-decimal list-inside space-y-4 text-sm text-gray-700">
                <li>
                  <span className="font-medium">Copie a URL abaixo:</span>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="flex-1 rounded-md bg-white border border-blue-200 px-3 py-2 text-xs sm:text-sm break-all">
                      {WEBHOOK_URL}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); copyUrl(); }}
                      className="shrink-0"
                    >
                      {urlCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                      {urlCopied ? 'Copiado!' : 'Copiar'}
                    </Button>
                  </div>
                </li>
                <li>
                  <span className="font-medium">Dentro da AtomosChat</span>, na conta do cliente, vá até a aba <strong>Ajustes</strong> e em seguida <strong>Webhooks</strong>.
                </li>
                <li>
                  Crie um novo webhook com o nome <strong>enviaBoleto</strong> e cole a URL copiada acima. Em eventos, marque <strong>&quot;Mensagem Recebida&quot;</strong> e salve.
                </li>
                <li>
                  Insira as palavras-chave para ativar esta automação (ex.: <strong>Baixe seu boleto</strong>). Essa palavra-chave é acionada tanto por resposta a template como por mensagem comum enviada, desde que seja igual às palavras cadastradas aqui.
                </li>
                <li>
                  Ative o webhook e salve.
                </li>
                <li>
                  <span className="font-medium">Pronto!</span> A automação fará o seguinte: buscar o boleto com base no contato cadastrado, buscando o último boleto vinculado a este número disponível.
                </li>
              </ol>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Configuração */}
      <Card className="border-2 border-amber-200 bg-amber-50/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <Zap className="w-5 h-5" />
            Webhook de Boleto (Automação)
          </CardTitle>
          <CardDescription className="text-amber-700">
            Quando o cliente enviar uma mensagem contendo alguma das palavras-chave abaixo, o sistema buscará boletos pelo telefone e enviará PIX + link do boleto via ÁtomosChat V2.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-white p-4">
            <div>
              <Label htmlFor="webhook-enabled" className="text-base font-medium">Ativar webhook de boleto</Label>
              <p className="text-sm text-gray-600 mt-1">Liga ou desliga a automação de busca e envio de boletos</p>
            </div>
            <Switch
              id="webhook-enabled"
              checked={isEnabled}
              onCheckedChange={setIsEnabled}
            />
          </div>

          <div className="space-y-3">
            <Label className="text-base font-medium">Palavras-chave que acionam o webhook</Label>
            <p className="text-sm text-gray-600">
              Se a mensagem do contato contiver alguma dessas palavras (ex.: &quot;Baixe seu boleto&quot;), o sistema buscará e enviará os boletos.
            </p>
            <div className="flex gap-2">
              <Input
                placeholder="Ex: Baixe seu boleto"
                value={newWord}
                onChange={(e) => setNewWord(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addWord())}
                className="max-w-xs"
              />
              <Button type="button" variant="outline" onClick={addWord} disabled={!newWord.trim()}>
                <Plus className="w-4 h-4 mr-2" />
                Adicionar
              </Button>
            </div>
            {triggerWords.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {triggerWords.map((word, index) => (
                  <span
                    key={`${word}-${index}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 text-sm"
                  >
                    {word}
                    <button
                      type="button"
                      onClick={() => removeWord(index)}
                      className="ml-1 hover:text-red-600 focus:outline-none"
                      aria-label="Remover"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {triggerWords.length === 0 && isEnabled && (
              <p className="text-sm text-amber-600">Adicione pelo menos uma palavra-chave para ativar a automação.</p>
            )}
          </div>

          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Salvar configuração
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-2 border-indigo-200 bg-indigo-50/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-indigo-800">
            <Bot className="w-5 h-5" />
            Chatbot AtomosChat v2
          </CardTitle>
          <CardDescription className="text-indigo-700">
            Selecione um chatbot para acoplar a todos os envios desta conexão (webhook de boleto, lembretes, cobranças, etc.). Selecione "Nenhum" para manter o comportamento padrão.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {chatbotsError ? (
            <p className="text-sm text-destructive">{chatbotsError}</p>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="chatbot-select">Chatbot</Label>
                <Select
                  value={chatbotId ?? 'none'}
                  onValueChange={(value) => setChatbotId(value === 'none' ? null : value)}
                  disabled={chatbotsLoading || savingChatbot}
                >
                  <SelectTrigger id="chatbot-select">
                    <SelectValue placeholder={chatbotsLoading ? 'Carregando...' : 'Nenhum'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum (padrão)</SelectItem>
                    {chatbots.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}
                      </SelectItem>
                    ))}
                    {chatbotId && !chatbots.find((b) => b.id === chatbotId) && (
                      <SelectItem value={chatbotId} disabled>
                        ⚠ {chatbotName || chatbotId} (indisponível)
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end">
                <Button onClick={handleSaveChatbot} disabled={savingChatbot || chatbotsLoading}>
                  {savingChatbot ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Salvar chatbot
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WebhooksTab;
